import type { GenericError, LoginFlow } from "@ory/client";
import { queryOptions, useQuery } from "@tanstack/vue-query";
import { isAxiosError } from "axios";
import { TanstackError } from "@saflib/sdk";
import type { Ref } from "vue";
import { getKratosFrontendApi } from "../kratos-client.ts";
import { SessionAlreadyAvailable, UnhandledResponse } from "../flow-results.ts";

export class LoginFlowCreated {
  constructor(readonly flow: LoginFlow) {}
}

export function createLoginFlowQueryKey(
  returnTo?: string,
  refresh?: boolean,
  aal?: string,
) {
  return [
    "kratos",
    "login",
    "create",
    `${returnTo ?? ""}:${refresh ? "1" : "0"}:${aal ?? ""}`,
  ] as const;
}

interface CreateLoginFlowQueryOptions {
  returnTo?: string;
  refresh?: boolean;
  /** Kratos `aal` query (e.g. `aal2`) to request step-up / MFA for an existing session. */
  aal?: string;
  enabled?: Ref<boolean>;
}

export function createLoginFlowQueryOptions({
  returnTo,
  refresh,
  aal,
  enabled,
}: CreateLoginFlowQueryOptions) {
  return queryOptions<
    LoginFlowCreated | SessionAlreadyAvailable | UnhandledResponse,
    TanstackError
  >({
    queryKey: createLoginFlowQueryKey(returnTo, refresh, aal),
    queryFn: async () => {
      try {
        const params: { returnTo?: string; refresh?: boolean; aal?: string } =
          {};
        if (returnTo) params.returnTo = returnTo;
        if (refresh) params.refresh = true;
        if (aal) params.aal = aal;
        const res = await getKratosFrontendApi().createBrowserLoginFlow(params);
        return new LoginFlowCreated(res.data);
      } catch (e) {
        if (isAxiosError(e) && e.response) {
          const status = e.response.status;
          const raw = e.response.data;
          if (status === 400) {
            const data = raw as { error?: GenericError };
            if (data.error?.id === "session_already_available") {
              return new SessionAlreadyAvailable(data.error);
            }
          }
          if (status >= 400 && status < 500) {
            return new UnhandledResponse(status, raw);
          }
        }
        if (isAxiosError(e)) throw new TanstackError(e.response?.status ?? 0);
        throw e;
      }
    },
    staleTime: 30_000,
    enabled,
  });
}

export function useCreateLoginFlowQuery(opts: CreateLoginFlowQueryOptions) {
  return useQuery(createLoginFlowQueryOptions(opts));
}
